/**
 * ComparisonMatrixBlock - Display a comparison matrix with row/column headers
 */

import React from 'react'
import { ComparisonMatrixBlock as ComparisonMatrixBlockType } from '../../../../lib/cockpit/schemas'
import {
  ComparisonLegend,
  getComparisonDisplayValue,
  getComparisonPresentation,
} from '../comparisonSemantics'

interface ComparisonMatrixBlockProps {
  data: ComparisonMatrixBlockType['data']
  blockId: string
  compact?: boolean
}

function ComparisonMatrixBlock({ data, blockId: _blockId, compact: _compact }: ComparisonMatrixBlockProps) {
  const { row_headers, column_headers, cells } = data

  if (!row_headers || !column_headers || !cells) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-sm italic">
        No comparison data available
      </div>
    )
  }

  return (
    <div className="space-y-3 overflow-x-auto">
      <ComparisonLegend />
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="py-3 px-4 text-left font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              {/* Empty corner cell */}
            </th>
            {column_headers.map((header: string, idx: number) => (
              <th
                key={idx}
                className="py-3 px-4 text-left font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {row_headers.map((rowHeader: string, rowIdx: number) => (
            <tr key={rowIdx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <td className="py-3 px-4 font-medium text-gray-700 dark:text-gray-300 bg-gray-50/50 dark:bg-gray-800/30">
                <div className="flex flex-col">
                  <span>{rowHeader}</span>
                  {getComparisonPresentation({ value: '', rowLabel: rowHeader }).preferenceLabel && (
                    <span className="mt-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                      {getComparisonPresentation({ value: '', rowLabel: rowHeader }).preferenceLabel}
                    </span>
                  )}
                </div>
              </td>
              {(cells[rowIdx] || []).map((cell: unknown, colIdx: number) => {
                const presentation = getComparisonPresentation({
                  value: cell,
                  rowLabel: rowHeader,
                  rowValues: cells[rowIdx] || [],
                })

                return (
                  <td key={colIdx} className={`py-3 px-4 text-gray-900 dark:text-white ${presentation.styles.cell}`}>
                    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${presentation.styles.badge}`}>
                      {renderCell(cell)}
                    </span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderCell(cell: unknown): React.ReactNode {
  if (typeof cell === 'boolean') return cell ? 'Yes' : 'No'
  return getComparisonDisplayValue(cell)
}

export default ComparisonMatrixBlock

